import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check } from 'lucide-react';
import { useOrganizationStore } from '../../stores/organizationStore';
import { useAuthStore } from '../../stores/authStore';
import { useOrgUnreadCounts } from '../../contexts/NotificationStateContext';
import { useTranslation } from 'react-i18next';

export function OrganizationSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const { activeOrganizationId, organizations, setActiveOrganization, fetchOrganizations } = useOrganizationStore();
  const { orgCounts, clearOrgCount } = useOrgUnreadCounts();
  const { t } = useTranslation();
  const activeOrg = organizations.find(org => org.id === activeOrganizationId);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = async (orgId: string) => {
    setIsOpen(false);
    if (orgId !== activeOrganizationId) {
      // Optimistically clear the unread count for the org being switched to
      clearOrgCount(orgId);
      await setActiveOrganization(orgId);
      await checkAuth();
      await fetchOrganizations();
    }
  };

  if (!activeOrg && organizations.length === 0) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center p-2 min-h-[44px] min-w-[44px] sm:min-w-0 sm:justify-start sm:px-3 sm:py-2 sm:gap-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium transition-colors flex-shrink-0"
        aria-label={activeOrg?.name ?? t('organizations.selector.select')}
      >
        {/* Mobile: Building2 icon only (44px touch target) */}
        <Building2 className="w-4 h-4 sm:hidden flex-shrink-0" />
        {/* Desktop (sm+): dot indicator */}
        <span className="hidden sm:block w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"></span>
        {/* Desktop (sm+): organization name */}
        <span className="hidden sm:inline max-w-[150px] truncate text-sm">
          {activeOrg?.name ?? t('organizations.selector.selectShort')}
        </span>
        {/* Desktop (sm+): chevron dropdown indicator */}
        <svg className={`hidden sm:block w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 mt-1 w-56 sm:w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50">
          <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {t('organizations.list.title')}
          </div>

          <div className="max-h-56 overflow-y-auto">
            {organizations.map(org => (
              <button
                key={org.id}
                onClick={() => handleSwitch(org.id)}
                className={`w-full px-3 py-3 sm:py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors min-h-[44px] ${
                  org.id === activeOrganizationId
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${org.id === activeOrganizationId ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-500'}`}></span>
                <span className="flex-1 truncate text-sm">{org.name}</span>
                {org.id === activeOrganizationId && <Check className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />}
                {/* Red dot for orgs with unread notifications that are NOT the currently active org */}
                {orgCounts[org.id] > 0 && org.id !== activeOrganizationId && (
                  <span className="ml-auto w-2 h-2 bg-red-500 rounded-full flex-shrink-0" title={t('organizations.unreadNotifications')} />
                )}
                {org.isPersonal && <span className="text-xs text-gray-400">{t('organizations.selector.personal')}</span>}
              </button>
            ))}
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-1 px-2">
            <button
              onClick={() => { setIsOpen(false); navigate('/organizations'); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Building2 className="w-4 h-4 text-indigo-500" />
              {t('organizations.list.manage')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
